// Simple Machines: lever, ramp, pulley (and wheel and axle at the top). Real mechanical
// advantage, friction ignored. Lesson, then do it.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { leverEffort, rampForce, pulleyPull, pulleyRope, wheelAxleForce } from './machines';

type Tab = 'lever' | 'ramp' | 'pulley' | 'wheel';

interface Step {
  tab: Tab; lesson: string; task: string;
  kind: 'check' | 'choice';
  check?: (m: Machines) => boolean;
  hint?: (m: Machines) => string;
  choices?: string[]; answer?: number;
  why: string;
}

interface Machines {
  // lever: beam of length 10 units; boulder at the left end (x=0); fulcrum at f; effort at the right end (x=10)
  W: number; f: number; effort: number; tilt: number; lifted: boolean;
  // ramp
  crateW: number; angle: number; push: number; crateT: number;  // crateT 0..1 up the ramp
  // pulley
  pianoW: number; segments: 1 | 2 | 4; pull: number; pianoH: number; ropePulled: number;
  // wheel and axle
  lidW: number; rWheel: number; wheelForce: number; opened: boolean;
}

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

const STEPS: Record<1 | 2 | 3, Step[]> = {
  1: [
    { tab: 'lever', lesson: 'A lever is a beam on a point called the fulcrum. The farther your push is from the fulcrum, the less push you need. The boulder weighs 60.', task: 'Move the fulcrum and add effort weights until the boulder lifts, using an effort of 20 or less.', kind: 'check', check: m => m.lifted && m.effort <= 20, hint: m => !m.lifted ? (m.effort === 0 ? 'Add some effort weights on the right.' : 'Not lifting yet. Slide the fulcrum closer to the boulder so your side is longer.') : 'It lifted, but with too much effort. Slide the fulcrum toward the boulder and take weights off.', why: 'Your side was three times longer, so you needed a third of the weight. Effort times distance equals load times distance.' },
    { tab: 'ramp', lesson: 'A ramp lets you lift something heavy a little at a time. The gentler the slope, the smaller the push, but the longer the path. The crate weighs 100. (We ignore friction.)', task: 'Set the ramp angle and your push so the crate reaches the top with a push under 40.', kind: 'check', check: m => m.crateT >= 1 && m.push < 40, hint: m => m.push >= 40 ? 'Your push is 40 or more. Make the ramp gentler so a smaller push works.' : 'The crate is not moving. Either push harder or make the ramp gentler.', why: 'A gentle ramp needs less push: push = weight x sin(angle). You traded distance for force.' },
    { tab: 'pulley', lesson: 'A pulley with more rope segments holding the load splits the weight between them. The piano weighs 80.', task: 'Pick the pulley and a pull under 30 that still lifts the piano.', kind: 'check', check: m => m.pianoH >= 1 && m.pull < 30, hint: m => m.pull >= 30 ? 'Pull under 30 means you need more rope segments sharing the load.' : 'Not lifting. Each segment holds pull x 1, so pull x segments must beat 80.', why: 'Four segments each carry a quarter of the weight, so a pull of 20 lifts 80.' },
  ],
  2: [
    { tab: 'lever', lesson: 'The lever rule is exact: effort x effort-distance = load x load-distance. The boulder weighs 60.', task: 'Lift the boulder with an effort of 12 or less.', kind: 'check', check: m => m.lifted && m.effort <= 12, hint: m => m.lifted ? 'Lifted, but with too much effort. Your side needs to be at least five times longer.' : 'Move the fulcrum very close to the boulder, then add effort.', why: '60 x 1.6 = 12 x 8. Your side was five times longer, so one fifth of the weight did it.' },
    { tab: 'pulley', lesson: 'Nothing is free. Fewer newtons of pull means more meters of rope.', task: 'You lifted the piano 2 meters with 4 segments. How much rope did you pull?', kind: 'choice', choices: ['8 meters', '2 meters', '4 meters', '16 meters'], answer: 0, why: 'Rope pulled = height x segments = 2 x 4 = 8 meters. A quarter of the force, four times the rope.' },
    { tab: 'ramp', lesson: 'Steep or gentle, the crate ends up at the same height.', task: 'Which ramp needs the smaller push for the same crate?', kind: 'choice', choices: ['The gentle, longer ramp', 'The steep, shorter ramp', 'They need the same push', 'Neither, ramps do not help'], answer: 0, why: 'Gentle means a small angle, and the push is weight x sin(angle). Small angle, small push, long walk.' },
  ],
  3: [
    { tab: 'ramp', lesson: 'Work is force times distance. A machine can shrink the force, but then the distance grows, and the work stays the same.', task: 'Half the push on a ramp means what for the distance you push?', kind: 'choice', choices: ['Twice the distance', 'Half the distance', 'The same distance', 'No distance at all'], answer: 0, why: 'Force x distance stays equal to weight x height. Halve one, double the other.' },
    { tab: 'wheel', lesson: 'A wheel and axle is a lever bent into a circle. Turning the big wheel turns the small axle with more force. The jar lid needs a twist of 60.', task: 'Set the wheel size so a force of 12 or less at the rim opens the lid.', kind: 'check', check: m => m.opened && m.wheelForce <= 12, hint: m => m.opened ? 'Opened, but with too much force. A bigger wheel needs less force.' : 'Not open yet. The force at the rim times the wheel size must beat 60 x the axle size.', why: 'Rim force = lid twist x axle radius / wheel radius. A wheel five times the axle needs a fifth of the force.' },
    { tab: 'pulley', lesson: 'Predict before you test.', task: 'If you double the rope segments, your pull becomes...', kind: 'choice', choices: ['Half as much', 'Twice as much', 'The same', 'Zero'], answer: 0, why: 'Pull = weight / segments. Double the segments, halve the pull, double the rope.' },
  ],
};

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const steps = STEPS[tier];
  let si = 0;
  let attempts = 0, correct = 0, missed = false, done = false;
  const t0 = performance.now();
  let tStep = performance.now();
  const m: Machines = { W: 60, f: 5, effort: 0, tilt: 0, lifted: false, crateW: 100, angle: 40, push: 30, crateT: 0, pianoW: 80, segments: 1, pull: 20, pianoH: 0, ropePulled: 0, lidW: 60, rWheel: 10, wheelForce: 10, opened: false };
  let tab: Tab = 'lever';

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const tabs = h('div.tabs', { style: { padding: '0 0 8px' } });
  const stage = h('div');
  const readout = h('p.center.muted.small', { style: { minHeight: '20px', fontWeight: '700' } });
  const controls = h('div.stack', { style: { gap: '6px' } });
  const checkBtn = h('button.btn.primary.block', { onclick: act }, 'Check');
  const choices = h('div.stack', { style: { gap: '8px' } });
  host.append(lesson, task, tabs, stage, readout, controls, checkBtn, choices);
  const S = makeSurface(stage, { aspect: 0.6, draw: false });

  function renderTabs() {
    clear(tabs);
    const list: Tab[] = done || tier === 3 ? ['lever', 'ramp', 'pulley', 'wheel'] : ['lever', 'ramp', 'pulley'];
    for (const t of list) tabs.append(h('button.tab' + (tab === t ? '.on' : ''), { onclick: () => { tab = t; renderTabs(); renderControls(); } }, t === 'lever' ? 'Lever' : t === 'ramp' ? 'Ramp' : t === 'pulley' ? 'Pulley' : 'Wheel'));
  }

  function slider(label: string, min: number, max: number, val: number, stepv: number, on: (v: number) => void): HTMLElement {
    const s = h('input', { type: 'range', min: String(min), max: String(max), step: String(stepv), value: String(val), 'aria-label': label }) as HTMLInputElement;
    const out = h('span', { style: { minWidth: '36px', textAlign: 'right', fontWeight: '800' } }, String(val));
    s.addEventListener('input', () => { const v = Number(s.value); out.textContent = String(v); on(v); });
    return h('div.row', null, h('span.muted.small', { style: { minWidth: '84px' } }, label), s, out);
  }

  function renderControls() {
    clear(controls);
    if (tab === 'lever') {
      controls.append(
        slider('Fulcrum spot', 1, 9, m.f, 0.5, v => { m.f = v; }),
        h('div.grid2', null,
          h('button.btn.small', { onclick: () => { m.effort = Math.max(0, m.effort - 4); } }, 'Take off 4'),
          h('button.btn.small', { onclick: () => { m.effort += 4; ctx.sound('pop'); } }, 'Add 4 effort'),
        ),
      );
    } else if (tab === 'ramp') {
      controls.append(
        slider('Ramp angle', 5, 70, m.angle, 1, v => { m.angle = v; m.crateT = 0; }),
        slider('Your push', 5, 100, m.push, 1, v => { m.push = v; }),
        h('button.btn.small.block', { onclick: () => { m.crateT = 0.001; } }, 'Push the crate'),
      );
    } else if (tab === 'pulley') {
      controls.append(
        h('div.grid3', null, ...([1, 2, 4] as const).map(n => h('button.btn.small' + (m.segments === n ? '.primary' : ''), { onclick: () => { m.segments = n; m.pianoH = 0; m.ropePulled = 0; renderControls(); } }, `${n} rope${n > 1 ? 's' : ''}`))),
        slider('Your pull', 5, 100, m.pull, 1, v => { m.pull = v; }),
        h('button.btn.small.block', { onclick: () => { m.pianoH = 0.001; m.ropePulled = 0; } }, 'Pull the rope'),
      );
    } else {
      controls.append(
        slider('Wheel size', 1, 10, m.rWheel, 1, v => { m.rWheel = v; m.opened = false; }),
        slider('Rim force', 2, 60, m.wheelForce, 1, v => { m.wheelForce = v; m.opened = false; }),
        h('button.btn.small.block', { onclick: () => { m.opened = m.wheelForce * m.rWheel >= m.lidW * 1 - 1e-9; if (!m.opened) ctx.say('The lid did not budge. More force, or a bigger wheel.'); else ctx.sound('pop'); } }, 'Twist'),
      );
    }
  }

  function showStep() {
    const st = steps[si];
    tab = st.tab;
    (lesson.firstElementChild as HTMLElement).textContent = st.lesson;
    task.textContent = `Step ${si + 1} of 3: ${st.task}`;
    clear(choices); missed = false; tStep = performance.now();
    checkBtn.style.display = st.kind === 'check' ? '' : 'none';
    if (st.kind === 'choice') st.choices!.forEach((c, i) => choices.append(h('button.choice', { style: { fontSize: '16px', minHeight: '52px', padding: '8px 12px' }, onclick: () => answer(i) }, c)));
    renderTabs(); renderControls();
    ctx.speak(st.task);
  }

  function pass(st: Step) {
    if (!missed) { attempts++; correct++; }
    ctx.report({ skill: 'physics', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `machines:${tier}:${si}` });
    ctx.sound('ding'); ctx.say(st.why);
    if (tier === 2 && si === 1) ctx.charm('charm.pulley');
    si++;
    if (si >= steps.length) {
      done = true;
      if (tier === 1) ctx.charm('charm.lever');
      task.textContent = 'Round complete. Every machine is open to play with.';
      checkBtn.style.display = 'none'; clear(choices); renderTabs();
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1500);
      return;
    }
    setTimeout(showStep, 1700);
  }
  function fail(hint: string) {
    if (!missed) { missed = true; attempts++; ctx.report({ skill: 'physics', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `machines:${tier}:${si}` }); }
    ctx.sound('buzz'); ctx.say(hint);
  }
  function act() { if (done) return; const st = steps[si]; if (st.kind !== 'check') return; if (st.check!(m)) pass(st); else fail(st.hint!(m)); }
  function answer(i: number) { if (done) return; const st = steps[si]; if (i === st.answer) pass(st); else fail('Think about force times distance, then try again.'); }

  function update(dt: number) {
    // lever: torque difference tilts the beam; lifted when effort torque beats load torque
    const d1 = m.f, d2 = 10 - m.f;
    const need = leverEffort(m.W, d1, d2);
    const diff = (m.effort * d2 - m.W * d1) / 600;
    const target = Math.max(-0.35, Math.min(0.35, diff));
    m.tilt += (target - m.tilt) * Math.min(1, dt * 4);
    m.lifted = m.effort >= need - 1e-9 && m.effort > 0;
    // ramp: crate climbs if push beats W sin(angle)
    if (m.crateT > 0 && m.crateT < 1) { if (m.push >= rampForce(m.crateW, m.angle)) m.crateT = Math.min(1, m.crateT + dt * 0.5); else { m.crateT = Math.max(0, m.crateT - dt * 0.6); if (m.crateT <= 0) ctx.say(`The crate slid back. It needs a push of at least ${Math.ceil(rampForce(m.crateW, m.angle))} on this slope.`); } }
    // pulley: lifts if pull x segments beats the piano
    if (m.pianoH > 0 && m.pianoH < 1) { if (m.pull >= pulleyPull(m.pianoW, m.segments)) { m.pianoH = Math.min(1, m.pianoH + dt * 0.4); m.ropePulled = pulleyRope(m.pianoH * 2, m.segments); } else { m.pianoH = 0; ctx.say(`Too weak. With ${m.segments} rope${m.segments > 1 ? 's' : ''} you need a pull of at least ${Math.ceil(pulleyPull(m.pianoW, m.segments))}.`); } }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    if (W < 16) return;
    c.fillStyle = '#eef3f7'; c.fillRect(0, 0, W, H);
    const g = H * 0.86;
    c.fillStyle = '#d9b98a'; c.fillRect(0, g, W, H - g);
    if (tab === 'lever') {
      const bx = W * 0.1, bw = W * 0.8, fx = bx + (m.f / 10) * bw, fy = g - 40;
      c.fillStyle = '#6b6b75'; c.beginPath(); c.moveTo(fx - 22, g); c.lineTo(fx, fy); c.lineTo(fx + 22, g); c.fill();
      c.save(); c.translate(fx, fy); c.rotate(-m.tilt);
      c.fillStyle = '#8a5a3b'; c.fillRect(-(fx - bx), -6, bw, 12);
      // boulder at left end, effort stack at right end
      c.fillStyle = '#9a9aa3'; c.beginPath(); c.arc(-(fx - bx) + 20, -28, 24, 0, 7); c.fill();
      c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText(`${m.W}`, -(fx - bx) + 20, -24);
      const n = Math.round(m.effort / 4);
      for (let i = 0; i < n; i++) { c.fillStyle = i % 2 ? '#f08a3c' : '#f5c542'; c.fillRect(bw - (fx - bx) - 34, -6 - (i + 1) * 12, 28, 11); }
      if (n) { c.fillStyle = '#2a2a2e'; c.fillText(`${m.effort}`, bw - (fx - bx) - 20, -10 - (n + 1) * 12); }
      c.restore();
      readout.textContent = `Load side ${m.f} long, your side ${(10 - m.f).toFixed(1)} long. Needs effort ${Math.ceil(leverEffort(m.W, m.f, 10 - m.f))}. ${m.lifted ? 'Lifted!' : ''}`;
    } else if (tab === 'ramp') {
      const a = (m.angle * Math.PI) / 180, len = W * 0.7, x0 = W * 0.15, hgt = Math.min(H * 0.7, len * Math.sin(a)), run = hgt / Math.tan(a);
      c.fillStyle = '#b48a5a'; c.beginPath(); c.moveTo(x0, g); c.lineTo(x0 + run, g - hgt); c.lineTo(x0 + run, g); c.fill();
      const cx = x0 + run * m.crateT, cy = g - hgt * m.crateT;
      c.save(); c.translate(cx, cy); c.rotate(-a); c.fillStyle = '#8a5a3b'; c.fillRect(0, -30, 34, 30); c.restore();
      c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'left'; c.fillText(`${m.angle} degrees`, x0, g - hgt - 10 < 14 ? 14 : g - hgt - 10);
      readout.textContent = `Push needed on this slope: ${Math.ceil(rampForce(m.crateW, m.angle))}. Your push: ${m.push}. ${m.crateT >= 1 ? 'At the top!' : ''}`;
    } else if (tab === 'pulley') {
      const topY = 24, px = W * 0.5;
      c.fillStyle = '#6b6b75'; c.fillRect(0, topY - 8, W, 8);
      const segs = m.segments, gap = 18;
      c.strokeStyle = '#8a5a3b'; c.lineWidth = 3;
      const pianoY = g - 30 - (g - 120) * m.pianoH;
      for (let i = 0; i < segs; i++) { const x = px - ((segs - 1) * gap) / 2 + i * gap; c.beginPath(); c.moveTo(x, topY); c.lineTo(x, pianoY); c.stroke(); }
      c.fillStyle = '#9a9aa3'; c.beginPath(); c.arc(px, topY + 6, 10, 0, 7); c.fill();
      c.fillStyle = '#2a2a2e'; c.fillRect(px - 40, pianoY, 80, 30); c.fillStyle = '#f6f3ec'; for (let i = 0; i < 7; i++) c.fillRect(px - 36 + i * 11, pianoY + 4, 8, 12);
      c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText(`${m.pianoW}`, px, pianoY + 46);
      // the pulling hand on the right
      c.strokeStyle = '#8a5a3b'; c.beginPath(); c.moveTo(px + 40, topY); c.lineTo(px + 40, topY + 40 + m.ropePulled * 10); c.stroke();
      c.fillStyle = '#e9b995'; c.beginPath(); c.arc(px + 40, topY + 48 + m.ropePulled * 10, 9, 0, 7); c.fill();
      readout.textContent = `${segs} rope${segs > 1 ? 's' : ''}: pull needed ${Math.ceil(pulleyPull(m.pianoW, segs))}. Rope pulled so far ${m.ropePulled.toFixed(1)} m. ${m.pianoH >= 1 ? 'Lifted!' : ''}`;
    } else {
      const cx = W * 0.5, cy = H * 0.5, rw = 20 + m.rWheel * 9, ra = 20;
      c.fillStyle = '#9a9aa3'; c.beginPath(); c.arc(cx, cy, rw, 0, 7); c.fill();
      c.fillStyle = m.opened ? '#5fbf6a' : '#e2574c'; c.beginPath(); c.arc(cx, cy, ra, 0, 7); c.fill();
      c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText(m.opened ? 'open' : 'lid', cx, cy + 5);
      c.strokeStyle = '#f08a3c'; c.lineWidth = 4; c.beginPath(); c.moveTo(cx + rw, cy); c.lineTo(cx + rw, cy - m.wheelForce * 1.2); c.stroke();
      readout.textContent = `Wheel ${m.rWheel} times the axle: rim force needed ${Math.ceil(wheelAxleForce(m.lidW, m.rWheel, 1))}. Yours: ${m.wheelForce}.`;
    }
  }

  const loop = makeLoop(update, draw);
  loop.start();
  showStep();
  return { destroy() { loop.stop(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.simple-machines',
  title: 'Simple Machines',
  blurb: 'Lever, ramp and pulley. Lift heavy things with small pushes, honestly.',
  category: 'science',
  skills: ['physics'],
  levelRange: [1, 10],
  unlock: { stars: 13 },
  mount,
};
export default mod;
